import { useRef } from "react";
import { Bar, CartesianGrid, ComposedChart, Legend, Line, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { ChartDownloadButton } from "@/components/common/ChartDownloadButton";
import { money, monthLong, monthShort } from "@/lib/format";

export const compact = (cents) => {
  const v = cents / 100;
  return Math.abs(v) >= 1000 ? `$${(v / 1000).toFixed(v % 1000 === 0 ? 0 : 1)}k` : `$${v.toFixed(0)}`;
};

function ChartTip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  const row = payload[0].payload;
  return (
    <div className="rounded-xl border border-border bg-card px-3.5 py-2.5 shadow-lg text-xs space-y-1">
      <div className="font-semibold text-sm mb-1">{monthLong(label)}</div>
      <div className="flex justify-between gap-6"><span className="text-emerald-700">Income</span><span className="font-num">{money(row.income_cents)}</span></div>
      <div className="flex justify-between gap-6"><span className="text-rose-600">Expenses</span><span className="font-num">{money(row.expense_cents)}</span></div>
      <div className="flex justify-between gap-6 border-t border-border pt-1"><span>Profit / loss</span><span className={`font-num font-semibold ${row.net_cents >= 0 ? "text-emerald-700" : "text-rose-600"}`}>{money(row.net_cents)}</span></div>
    </div>
  );
}

export function MonthlyChart({ data, filename = "DaLilShop_IncomeVsExpenses.png" }) {
  const ref = useRef(null);
  return (
    <div ref={ref} className="surface p-5 sm:p-6 h-full fade-up" data-testid="monthly-chart">
      <div className="flex flex-wrap items-baseline justify-between gap-2 mb-4">
        <h3 className="font-heading text-lg font-semibold">Income vs expenses</h3>
        <div className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground">Last 12 months</span>
          <ChartDownloadButton targetRef={ref} filename={filename} testId="monthly-chart-download-png" />
        </div>
      </div>
      <div className="h-[260px] -ml-2">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={data} margin={{ top: 5, right: 5, left: 0, bottom: 0 }}>
            <CartesianGrid vertical={false} stroke="hsl(200 14% 90%)" />
            <XAxis dataKey="month" tickFormatter={monthShort} tick={{ fontSize: 11 }} tickLine={false} axisLine={false} interval="preserveStartEnd" />
            <YAxis tickFormatter={compact} tick={{ fontSize: 11 }} tickLine={false} axisLine={false} width={48} />
            <Tooltip content={<ChartTip />} cursor={{ fill: "hsl(200 25% 95%)" }} />
            <Legend wrapperStyle={{ fontSize: 12 }} iconType="circle" />
            <Bar dataKey="income_cents" name="Income" fill="#10B981" radius={[4, 4, 0, 0]} maxBarSize={22} />
            <Bar dataKey="expense_cents" name="Expenses" fill="#F43F5E" radius={[4, 4, 0, 0]} maxBarSize={22} />
            <Line dataKey="net_cents" name="Profit / loss" stroke="#111111" strokeWidth={2.5} dot={{ r: 3, fill: "#FFC72C", stroke: "#111111" }} type="monotone" />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
      <div className="mt-5">
        <div className="eyebrow mb-2">Monthly profit / loss</div>
        <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-6 gap-2" data-testid="monthly-pl-grid">
          {data.map((m) => (
            <div key={m.month} className="rounded-xl bg-secondary/60 px-2.5 py-2" data-testid={`monthly-pl-${m.month}`}>
              <div className="text-[11px] text-muted-foreground">{monthShort(m.month)}</div>
              <div className={`font-num text-xs font-semibold truncate ${m.net_cents >= 0 ? "text-emerald-700" : "text-rose-600"}`}>{money(m.net_cents)}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
